import React, { Fragment } from 'react'
import moment from 'moment'

import {
  Box,
  Card,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Theme,
  TimesIcon,
  Typography,
  createStyles,
  makeStyles,
  useSnackbar,
} from '@forgeglobal/design-framework'

import { FileUploaderCard } from 'components/shared/FileUploaderCard'
import {
  OfferingFileFragment,
  useDeleteOfferingDataroomFileMutation,
  useGetOfferingFilesQuery,
  useUploadOfferingFilesMutation,
} from 'graphql/bindings/AdminBindings'
import { download } from 'util/DocumentUtils'

interface CampaignDataRoomProps {
  offeringId?: string
}

const useStyles = makeStyles(({ palette }: Theme) =>
  createStyles({
    documentTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      cursor: 'pointer',
    },
    dropping: {
      border: `5px dashed ${palette.divider}`,
    },
  })
)

export const CampaignDataRoom: React.FC<CampaignDataRoomProps> = ({ offeringId }) => {
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()

  const { data } = useGetOfferingFilesQuery({
    variables: {
      offeringId,
    },
  })

  const dataRoomFiles = data?.offering.files.filter(file => {
    return file.tags?.every(tag => tag.tag !== 'csv-upload')
  })

  const offeringFiles = dataRoomFiles ?? []

  const [associateFiles] = useUploadOfferingFilesMutation()

  const [deleteFile] = useDeleteOfferingDataroomFileMutation()

  const handleFileSerials = async (fileSerials: string[]) => {
    enqueueSnackbar('Uploading files to Box ...')
    return associateFiles({ variables: { offeringId, fileSerials } }).then(() =>
      enqueueSnackbar('Successfully uploaded files to Box')
    )
  }

  const removeDocument = (fileId: number, fileName: string) => {
    enqueueSnackbar(`Removing document ${fileName} ...`)
    deleteFile({ variables: { fileId, offeringId } }).then(() => enqueueSnackbar(`Document ${fileName} removed.`))
  }

  const handleClick = (file: OfferingFileFragment) => {
    enqueueSnackbar(`Downloading document ${file.filename} ...`)
    download(file)
  }

  return (
    <FileUploaderCard
      acceptedFileTypes=".pdf, .doc, .docx"
      title={
        <Typography variant="h6" color="secondary">
          Data Room
        </Typography>
      }
      isPaperclipVisible
      onFileSerials={handleFileSerials}>
      <Typography variant="subtitle1">Sample documents</Typography>
      <Box mb={2} mt={2}>
        <Typography variant="body1" fontWeight="light">
          Data room contains sample transaction documents and new client forms by default. You do not need to upload
          additional copies of these documents.
        </Typography>
      </Box>
      {!!offeringFiles.length && (
        <Fragment>
          <Typography variant="subtitle1">Offering documents</Typography>
          <Box mt={2}>
            <Box mb={2} mt={2}>
              {offeringFiles.map(file => {
                const { id, filename, date_created } = file
                const removeDoc = () => removeDocument(id, filename ?? 'Unknown file name')
                return (
                  <Card key={id}>
                    <CardHeader
                      secondaryIcon={
                        <Fragment>
                          <Chip label="Uploaded" color="secondary" />
                          <Box ml={2} mr={2}>
                            <Divider variant="fullWidth" orientation="vertical" />
                          </Box>
                        </Fragment>
                      }
                      action={
                        <IconButton>
                          <TimesIcon onClick={removeDoc} />
                        </IconButton>
                      }
                      title={
                        <div className={classes.documentTitle} onClick={() => handleClick(file)}>
                          <Typography variant="subtitle2">{filename}</Typography>
                          <Typography variant="subtitle2">{moment(date_created).format('ddd, l [at] LT')}</Typography>
                        </div>
                      }
                    />
                  </Card>
                )
              })}
            </Box>
          </Box>
        </Fragment>
      )}
    </FileUploaderCard>
  )
}
